<template>
    <div>
        <form @submit.prevent class="m-form" name="headsOfLossForm">
            <div class="flex-grid a-form-inputs">
                <div class="flex-grid__item">
                    <control for="general_damages_psla" label="General Damages (PSLA)" before-after="pound">
                        <input autocomplete="off" v-model.number="generalDamages"
                               name="general_damages_psla"
                               id="general_damages_psla"
                               v-field-control
                               v-on:focus="generalDamages = generalDamages || ''"
                               v-on:blur="generalDamages = generalDamages || 0"
                               v-two-dp
                               type="number">
                        <span class="a-form-inputs--help" v-if="diseaseType === 'Mesothelioma'">
                            Damages can be calculated using the calculator available
                            <a href="#" @click.prevent="toggleGeneralDamagesPslaModal()">
                                here
                            </a>
                        </span>
                    </control>
                    <control for="past_loss_of_services" label="Past Cost of Services" before-after="pound">
                        <input autocomplete="off" v-model.number="pastDependencyOnServices"
                               name="past_loss_of_services"
                               min="0"
                               v-on:focus="pastDependencyOnServices = pastDependencyOnServices || ''"
                               v-on:blur="pastDependencyOnServices = pastDependencyOnServices || 0"
                               v-two-dp
                               type="number">
                    </control>
                    <control for="future_loss_of_services" label="Future Cost of Services" before-after="pound">
                        <input autocomplete="off" v-model.number="futureDependencyOnServices"
                               name="future_loss_of_services"
                               min="0"v-on:focus="futureDependencyOnServices = futureDependencyOnServices || ''"
                               v-on:blur="futureDependencyOnServices = futureDependencyOnServices || 0"
                               v-two-dp
                               type="number">
                    </control>
                    <control for="past_cost_of_travel" label="Past Cost of Travel" before-after="pound">
                        <input autocomplete="off" v-model.number="pastCostOfTravel"
                               name="past_cost_of_travel"
                               min="0"
                               v-on:focus="pastCostOfTravel = pastCostOfTravel || ''"
                               v-on:blur="pastCostOfTravel = pastCostOfTravel || 0"
                               v-two-dp
                               type="number">
                    </control>
                    <control for="miscellaneous_costs"
                             label="Miscellaneous - Medication, Appliances, Additional Food & Energy Costs"
                             before-after="pound">
                        <input autocomplete="off" v-model.number="misc"
                               name="miscellaneous_costs"
                               min="0"
                               v-on:focus="misc = misc || ''"
                               v-on:blur="misc = misc || 0"
                               v-two-dp
                               type="number">
                    </control>
                    <div v-for="index in customHeadsOfLoss.length">
                        <head-of-loss @update="updateValid" :index="index - 1"></head-of-loss>
                    </div>

                    <button class="a-button a-button--no-margin u-r-margin-top-20 u-r-margin-bottom-20" @click="addCustomHeadOfLoss">
                        Add Another Head of Loss
                    </button>
                </div>
                <div class="flex-grid__item flex-grid--space-left">
                    <control label="Cost of Care"
                             tip-text="You have multiple options to assess the care claim — click the help icon to find out more. "
                             tip-icon="info"
                             tip-side="left"
                             tip-width="250"
                             tip-name="past_care"
                             modalName="careModal"
                             for="cost_of_care">
                        <select name="cost_of_care" v-model="costOfCareType" v-field-control required>
                            <option :value="null" disabled hidden>Please select...</option>
                            <option value="auto">Auto-calculate</option>
                            <option value="own">Enter past and future cost of care</option>
                        </select>
                    </control>

                    <control for="care_total" label="Care Total" v-if="costOfCareType === 'auto'"
                             before-after="pound"
                             :tip-icon="careIcon"
                             :tip-show="costOfCareType === 'auto'"
                             tip-text="Click to view calculation" tip-side="left"
                             tip-width="140"
                             tip-name="care_total"
                             :modal-name="'headsOfLoss_' + uniqueId"
                             :endpoint="'autoCare'">
                        <input autocomplete="off" :value="currencyFormat(pastTotalValue, '')" type="text"
                               disabled>
                    </control>

                    <div v-if="costOfCareType === 'own'">
                        <hr style="margin-bottom: 40px"/>

                        <control label="Past Care"
                                 for="past_care_type">
                            <select id="pastCareCalcType" v-model="pastCareCalcType">
                                <option value="own">Enter your own figure</option>
                                <option value="manual">Detailed calculation</option>
                            </select>
                            <control-error :show="$error('past_care_type')">
                                Please select a care type
                            </control-error>
                        </control>

                        <div v-for="index in pastCarePeriods.length" v-if="pastCareCalcType === 'manual'">
                            <period @update="updateValid" :index="index - 1" :type="'past'"></period>
                        </div>
                        <button class="a-button a-button--no-margin"
                                v-if="pastCarePeriods.length >= 0 && pastCareCalcType === 'manual'"
                                @click="addNewCarePeriod('past')"
                                style="margin-bottom: 40px;">
                            Add Period
                        </button>
                        <control for="past_care_total" label="Past Care Total"
                                 before-after="pound"
                                 :tip-icon="careIcon"
                                 tip-text="Click to view calculation" tip-side="left"
                                 :modal-name="'headsOfLoss_' + uniqueId"
                                 :endpoint="'autoCare'">
                            <input autocomplete="off" v-model.number="pastTotalValue"
                                   type="text"
                                   v-on:focus="pastTotalValue = pastTotalValue || ''"
                                   v-on:blur="pastTotalValue = pastTotalValue || 0"
                                   v-two-dp
                                   :disabled="pastCareCalcType !== 'own'">
                        </control>
                    </div>

                    <div v-if="costOfCareType === 'own'">
                        <hr style="margin-bottom: 40px"/>

                        <control label="Future Care"
                                 for="future_care_type">
                            <select id="futureCareCalcType" v-model="futureCareCalcType">
                                <option value="own">Enter your own figure</option>
                                <option value="manual">Detailed calculation</option>
                            </select>
                            <control-error :show="$error('future_care_type')">
                                Please select a care type
                            </control-error>
                        </control>
                        <div v-for="index in futureCarePeriods.length" v-if="futureCareCalcType === 'manual'">
                            <period @update="updateValid" :index="index - 1" :type="'future'"></period>
                        </div>
                        <button class="a-button a-button--no-margin"
                                v-if="futureCarePeriods.length >= 0 && futureCareCalcType === 'manual'"
                                @click="addNewCarePeriod('future')"
                                style="margin-bottom: 40px;">
                            Add Period
                        </button>
                        <control for="future_care_total" label="Future Care Total"
                                 before-after="pound">
                            <input autocomplete="off" v-model.number="futureTotalValue"
                                   type="text"
                                   v-on:focus="futureTotalValue = futureTotalValue || ''"
                                   v-on:blur="futureTotalValue = futureTotalValue || 0"
                                   v-two-dp
                                   :disabled="futureCareCalcType !== 'own'">
                        </control>

                        <hr style="margin-bottom: 40px"/>

                        <control label="Total Cost of Care"
                                 before-after="pound">
                            <input autocomplete="off" :value="pastTotalValue+futureTotalValue"
                                   type="text"
                                   v-two-dp
                                   disabled>
                        </control>
                    </div>
                </div>
            </div>
        </form>

        <modal name="careModal" no-default-button="true">
            <h2>
                Care Claim Options
            </h2>
            <p>
                You have 2 options to assess the care claim:<br>
            </p>
            <ul class="u-margin-lr-40">
                <li class="u-margin-bottom-20">
                    You can Auto-calculate the award. The CA in Rothwell v Chemical Insulating Co. Ltd [2006] EWCA Civ 27 recognised a sum of £12,500 as the norm for care in claims for terminal malignant mesothelioma. This award is inflated to today’s value applying the Retail Price Index since January 2006.
                </li>
                <li class="u-margin-bottom-20">
                    You can carry out separate calculations for past and future care. These are:
                </li>
                <ul class="u-margin-lr-40 u-margin-top-25">
                    <li class="u-margin-bottom-20">
                        You can simply enter in a £ sum for the care.
                    </li>
                    <li class="u-margin-bottom-20">
                        You can carry out a detailed calculation looking at differing periods and differing hours of care and applying either a basic or aggregate care rate valued according to Local Authority Spinal Point 8. You can then discount the claim by 25% to reflect the gratuitous rather than commercial nature of the care.
                    </li>
                </ul>
            </ul>
        </modal>

        <modal :name="'headsOfLoss_' + uniqueId" no-default-button="true">
            <modal-content></modal-content>
        </modal>

        <modal name="generalDamagesPslaModal" no-default-button="true">
            <general-damages-psla-calculator v-if="modals.generalDamagesPslaModal"></general-damages-psla-calculator>
        </modal>
        
        <step-button :next-valid="isValid()"></step-button>
    </div>
</template>
<script>
    import Control from 'components/form/Control.vue';
    import ControlError from 'components/form/control/Error.vue';
    import currencyFormat from 'mixins/currencyFormat';
    import FormValidator from 'components/form/validator/mixin';
    import HeadOfLoss from 'pages/lost-years-claims/steps/heads-of-loss/HeadOfLoss.vue';
    import Modal from 'components/modal/Modal.vue';
    import ModalContent from 'components/modal/ModalContent.vue';
    import Period from 'pages/lost-years-claims/steps/heads-of-loss/Period.vue';
    import StepButton from 'components/StepButton.vue';
    import {setStepValid} from 'services/routingMethods';
    import {mapActions, mapGetters} from 'vuex';
    import TwoDecimalPlaces from 'directives/TwoDecimalPlaces';
    import GeneralDamagesPslaCalculator from 'pages/lost-years-claims/steps/components/GeneralDamagesPslaCalculator.vue';

    export default {
        name: 'other-heads-of-loss',
        mixins: [
            FormValidator,
            currencyFormat
        ],
        components: {
            Control,
            ControlError,
            StepButton,
            HeadOfLoss,
            Period,
            Modal,
            ModalContent,
            GeneralDamagesPslaCalculator
        },
        computed: {
            ...mapGetters('lost/headsOfLoss', [
                'getHeadsOfLossValue',
                'getCareValue',
                'allHeadsOfLossValid',
                'allCarePeriodsValid'
            ]),
            ...mapGetters('lost/claimant', ['getClaimantDetail']),
            ...mapGetters('modal', ['modals']),
            generalDamages: {
                get() {
                    return this.getHeadsOfLossValue('generalDamages')
                },
                set(value) {
                    this.setHeadsOfLossValue({key: 'generalDamages', value: value})
                        .then(this.updateValid);
                }
            },
            pastCostOfTravel: {
                get() {
                    return this.getHeadsOfLossValue('pastCostOfTravel')
                },
                set(value) {
                    this.setHeadsOfLossValue({key: 'pastCostOfTravel', value: value})
                        .then(this.updateValid);
                }
            },
            pastDependencyOnServices: {
                get() {
                    return this.getHeadsOfLossValue('pastDependencyOnServices')
                },
                set(value) {
                    this.setHeadsOfLossValue({key: 'pastDependencyOnServices', value: value})
                        .then(this.updateValid);
                }
            },
            futureDependencyOnServices: {
                get() {
                    return this.getHeadsOfLossValue('futureDependencyOnServices')
                },
                set(value) {
                    this.setHeadsOfLossValue({key: 'futureDependencyOnServices', value: value})
                        .then(this.updateValid);
                }
            },
            misc: {
                get() {
                    return this.getHeadsOfLossValue('misc')
                },
                set(value) {
                    this.setHeadsOfLossValue({key: 'misc', value: value})
                        .then(this.updateValid);
                }
            },
            customHeadsOfLoss: {
                get() {
                    return this.getHeadsOfLossValue('customHeadsOfLoss')
                },
            },
            pastCarePeriods: {
                get() {
                    return this.getCareValue('past').periods
                },
            },
            futureCarePeriods: {
                get() {
                    return this.getCareValue('future').periods
                },
            },
            costOfCareType: {
                get() {
                    return this.getCareValue('type');
                },
                set(value) {
                    this.setCareValue({key: 'type', value})
                        .then(this.updateValid);

                    if (value === 'auto') {
                        return this.autoCalculateCare();
                    }

                    this.setCareValue({key: 'totalValue', value: 0, type: 'past'})
                        .then(this.updateValid());
                }
            },
            pastCareCalcType: {
                get() {
                    return this.getCareValue('past').type
                },
                set(value) {
                    this.setCareValue({key: 'type', value, type: 'past'})
                        .then(this.updateValid);
                }
            },
            futureCareCalcType: {
                get() {
                    return this.getCareValue('future').type
                },
                set(value) {
                    this.setCareValue({key: 'type', value, type: 'future'})
                        .then(this.updateValid());
                }
            },
            pastTotalValue: {
                get() {
                    return this.getCareValue('past').totalValue
                },
                set(value) {
                    if (isNaN(value)) {
                        value = 0;
                    }
                    this.setCareValue({key: 'totalValue', value, type: 'past'})
                        .then(this.updateValid());
                }
            },
            futureTotalValue: {
                get() {
                    return this.getCareValue('future').totalValue
                },
                set(value) {
                    if (isNaN(value)) {
                        value = 0;
                    }
                    this.setCareValue({key: 'totalValue', value, type: 'future'})
                        .then(this.updateValid());
                }
            },
            careIcon: {
                get() {
                    return (this.getCareValue('type') === 'auto') ? 'formula' : 'default';
                }
            },
            diseaseType: {
                get() {
                    return this.getClaimantDetail('diseaseType');
                }
            },
            uniqueId() {
                return this._uid;
            }
        },
        directives: {
            'two-dp': TwoDecimalPlaces
        },
        watch: {
            'costOfCareType'(newVal, oldValue) {
                this.$nextTick(() => {
                    this.updateValid()
                });
            }
        },
        methods: {
            ...mapActions('lost/headsOfLoss', [
                'setHeadsOfLossValue',
                'setCareValue',
                'addCustomHeadOfLoss',
                'addCarePeriod',
                'autoCalculateCare',
            ]),
            ...mapActions('modal', ['toggle']),
            addNewCarePeriod(type) {
                this.addCarePeriod(type)
            },
            updateValid() {
                setStepValid(this.isValid());
            },
            isValid() {
                if (this.costOfCareType === 'auto' || this.costOfCareType === 'own') {
                    this.validCare = this.allHeadsOfLossValid;
                }

                if (this.pastCareCalcType === 'manual') {
                    this.validCare = this.allHeadsOfLossValid && this.allCarePeriodsValid('past');
                }

                if (this.futureCareCalcType === 'manual') {
                    this.validCare = this.allHeadsOfLossValid && this.allCarePeriodsValid('future');
                }

                return (
                    this.$valid()
                    && this.validCare
                    && this.costOfCareType !== null
                );
            },
            toggleGeneralDamagesPslaModal() {
                this.toggle('generalDamagesPslaModal');
            },
        }
    }
</script>



// WEBPACK FOOTER //
// HeadsOfLoss.vue?0fde70a9